import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { ConfirmDialog } from '../ui/ConfirmDialog';

export interface InvoiceGap {
  pos_id: string;
  pos_name: string;
  missing: string[];
}

interface InvoiceIncompleteDialogProps {
  open: boolean;
  gaps: InvoiceGap[];
  onClose: () => void;
  onForce: () => Promise<void>;
}

export const InvoiceIncompleteDialog: React.FC<InvoiceIncompleteDialogProps> = ({
  open,
  gaps,
  onClose,
  onForce,
}) => {
  const [confirmForce, setConfirmForce] = useState(false);

  return (
    <>
      <Dialog.Root
        open={open && !confirmForce}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-[60] max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100">
                  <AlertTriangle size={24} className="text-orange-600" />
                </div>
                <Dialog.Title className="text-xl font-black text-gray-900">
                  Billing data incomplete
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="text-sm text-gray-600 mb-4">
              Fill the PoS billing fields, or finalize anyway. Missing fields
              print as N/A on the PDF. PoS master data is not changed.
            </Dialog.Description>
            <div className="space-y-3 mb-6">
              {gaps.map((gap) => (
                <div key={gap.pos_id || gap.pos_name} className="bg-gray-50 rounded-xl p-3">
                  <p className="font-bold text-gray-900 text-sm">{gap.pos_name}</p>
                  <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
                    {gap.missing.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setConfirmForce(true)}
                className="min-h-[48px] px-4 bg-orange-600 text-white rounded-lg font-bold"
              >
                Finalize anyway
              </button>
              <button
                type="button"
                onClick={onClose}
                className="min-h-[48px] px-4 bg-gray-200 text-gray-700 rounded-lg font-bold"
              >
                Cancel
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <ConfirmDialog
        open={confirmForce}
        onOpenChange={setConfirmForce}
        variant="warning"
        title="Finalize anyway?"
        message="Manufacturer / distributor already has this data. Missing billing fields will show as N/A on the PDF."
        confirmText="Finalize anyway"
        onConfirm={async () => {
          await onForce();
          setConfirmForce(false);
          onClose();
        }}
      />
    </>
  );
};
